import React, { useContext } from 'react';
import { Button, Table, Badge } from 'reactstrap';
import i18n, { formatNumber, getDateFormatOptions } from '@/utils/i18n';
import { Link } from 'react-router-dom';
import { OrderFulfillmentReducedFragment } from '../../../graphql/fragments/orderFulfillmentReduced.generated';
import CustomPagination from '../../../components/CustomPagination';
import { FaEdit } from 'react-icons/fa';
import {
  IsVytalEmployee,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import OrderfulfillmentStatusBadge from '../../../components/Orderfulfillment/OrderfulfillmentStatusBadge';
import { Routes } from '@/utils/routes';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { DEFAULT_CONTAINER_TYPE_ICON, getImageUrl } from '@/utils/imageUrl';

type RowContainerType = {
  id: number;
  name: string;
  image: string | null | undefined;
};

function getUsedContainerTypes(
  of: OrderFulfillmentReducedFragment,
): RowContainerType[] {
  const map = new Map<number, RowContainerType>();
  const add = (c: {
    container_type_id: number;
    container_type_name: string;
    container_type_image?: string | null;
  }) => {
    if (!map.has(c.container_type_id)) {
      map.set(c.container_type_id, {
        id: c.container_type_id,
        name: c.container_type_name,
        image: c.container_type_image,
      });
    }
  };

  of.container_stations.forEach((cs) => {
    cs.containers.forEach(add);
    cs.manual_amounts.forEach(add);
  });
  of.single_containers.forEach(add);
  of.manual_amounts.forEach(add);
  of.goodwill.forEach(add);

  return Array.from(map.values());
}

export type OrderFulfillmentsTableHeaderSort =
  | 'detail'
  | 'status'
  | 'eventAccount'
  | 'isBillable'
  | 'type'
  | 'signer'
  | 'itemLocation'
  | 'event'
  | 'timestamp'
  | 'itemCount';

type OrderFulfillmentsTableProps = {
  orderFulfillments: OrderFulfillmentReducedFragment[];
  loading: boolean;
  error: any;
  setPage: (page: number) => void;
  page: number;
  pageCount: number;
  headers?: OrderFulfillmentsTableHeaderSort[];
};

const OrderFulfillmentsTable = ({
  orderFulfillments,
  loading,
  error,
  setPage,
  page,
  pageCount,
  headers = [
    'detail',
    'status',
    'isBillable',
    'eventAccount',
    'type',
    'signer',
    'itemLocation',
    'event',
    'timestamp',
    'itemCount',
  ],
}: OrderFulfillmentsTableProps) => {
  const viewer = useContext(ViewerContext);
  const isInternal = IsVytalEmployee(viewer);

  function getItemCount(orderFulfillment: OrderFulfillmentReducedFragment) {
    const containerStations = orderFulfillment.container_stations;
    const containersAmounts = containerStations
      .map(
        (cs) =>
          cs.containers.length +
          cs.manual_amounts.reduce(
            (acc, ma) => acc + ma.amount + ma.amount_sealed,
            0,
          ) +
          1,
      ) // +1 for the station itself
      .concat(orderFulfillment.goodwill.map((g) => g.amount + g.amount_sealed))
      .concat(
        orderFulfillment.manual_amounts.map((g) => g.amount + g.amount_sealed),
      );

    return containersAmounts.reduce(
      (a, b) => a + b,
      orderFulfillment.single_containers.length +
        orderFulfillment.assets.length,
    );
  }

  const colSpan = headers.length;

  const headerRenderers: Record<
    OrderFulfillmentsTableHeaderSort,
    (of: OrderFulfillmentReducedFragment) => React.ReactNode
  > = {
    detail: (of) => (
      <td key="detail">
        <Link to={Routes.orderFulfillment(of.id)}>
          <Button>
            <FaEdit />
          </Button>
        </Link>
      </td>
    ),
    status: (of) => (
      <td key="status">
        <OrderfulfillmentStatusBadge status={of.status} />
      </td>
    ),
    isBillable: (of) => (
      <td key="isBillable">
        {of.is_billable ? i18n.t('Common:yes') : i18n.t('Common:no')}
      </td>
    ),
    eventAccount: (of) => (
      <td key="eventAccount">
        {isInternal ? (
          <>
            <Link to={Routes.eventAccountDetail(of.event_account.id)}>
              {of.event_account.name}
            </Link>
            {of.event_account.is_deleted && (
              <Badge color="danger" className="ms-2">
                DELETED
              </Badge>
            )}
          </>
        ) : (
          <>
            {of.event_account.name}
            {of.event_account.is_deleted && (
              <Badge color="danger" className="ms-2">
                DELETED
              </Badge>
            )}
          </>
        )}
      </td>
    ),
    type: (of) => (
      <td key="type">
        {i18n.t(`OrderFulfillmentType:${of.type?.toUpperCase()}`)}
      </td>
    ),
    signer: (of) => (
      <td key="signer">
        {isInternal ? (
          <Link to={Routes.authorizedSigner(of.authorized_signer_id ?? '')}>
            {of.authorized_signer_name}
          </Link>
        ) : (
          of.authorized_signer_name
        )}
      </td>
    ),
    itemLocation: (of) => (
      <td key="itemLocation">
        {isInternal ? (
          <Link to={Routes.itemLocation(of.item_location.id)}>
            {of.item_location.name}
          </Link>
        ) : (
          of.item_location.name
        )}
      </td>
    ),
    event: (of) => (
      <td key="event">
        {isInternal ? (
          <>
            <Link to={Routes.eventDetail(of.event.id)}>{of.event.name}</Link>
            {of.event.is_deleted && (
              <Badge color="danger" className="ms-2">
                DELETED
              </Badge>
            )}
          </>
        ) : (
          <>
            {of.event.name}
            {of.event.is_deleted && (
              <Badge color="danger" className="ms-2">
                DELETED
              </Badge>
            )}
          </>
        )}
      </td>
    ),
    timestamp: (of) => (
      <td key="timestamp">
        {new Date(of.timestamp).toLocaleDateString(
          i18n.language,
          getDateFormatOptions(true),
        )}
      </td>
    ),
    itemCount: (of) => {
      const usedTypes = getUsedContainerTypes(of);
      return (
        <td key="itemCount" className="numbers-formatted">
          <div>{formatNumber(getItemCount(of))}</div>
          {usedTypes.length > 0 && (
            <div className="tw:flex tw:flex-wrap tw:gap-1 tw:mt-1 tw:justify-end">
              {usedTypes.map((ct) => (
                <Tooltip key={ct.id}>
                  <TooltipTrigger asChild>
                    <img
                      src={getImageUrl(
                        24,
                        ct.image || DEFAULT_CONTAINER_TYPE_ICON,
                      )}
                      alt={ct.name}
                      className="tw:w-6 tw:h-6 tw:object-contain tw:rounded tw:border tw:border-gray-300"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    {ct.name} (id: {ct.id})
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </td>
      );
    },
  };

  return (
    <div>
      <Table striped bordered responsive>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{i18n.t(`OrderFulfillment:${header}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={colSpan}>{i18n.t('Common:loading')}...</td>
            </tr>
          )}

          {error && (
            <tr>
              <td colSpan={colSpan}>{i18n.t('Common:error')}</td>
            </tr>
          )}

          {!loading && !error && orderFulfillments.length === 0 && (
            <tr>
              <td colSpan={colSpan}>{i18n.t('Common:noItems')}</td>
            </tr>
          )}

          {orderFulfillments.map((of) => (
            <tr key={of.id}>
              {headers.map((header) => headerRenderers[header](of))}
            </tr>
          ))}
        </tbody>
      </Table>

      <CustomPagination
        pageCount={pageCount}
        currentPage={page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default OrderFulfillmentsTable;
